import * as Sentry from '@sentry/react'

import React, { useCallback, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { BLUE_100, BLUE_300, GRAY_400, GRAY_900 } from 'constants/styling/theme'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { Box } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { MUIDivider } from 'components/common/MUIDivider'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import type { GroupedParcels } from './parcel.types'
import styles from './ParcelViewerMap.module.sass'

/** @interface Props for the ParcelViewerMap component. */
interface Props {
  /** The latitude and longitude of the selected property location. */
  selectedPropertyLocation?: { lat: number, lng: number }
  /** Callback function triggered when the parcel selection changes. */
  onParcelChange?: (groupedParcels: GroupedParcels, selection: string[]) => void
  /** Callback function triggered when the parcel viewer help popup is opened. */
  onOpenParcelViewerPopup?: () => void
}

/**
 * @component
 * ParcelViewerMap integrates an iframe-based DocEstate parcel map viewer.
 * Posts parcel selections back via `window.postMessage`; grouped by
 * district → section → parcel numbers and handed to `onParcelChange`.
 *
 * @example
 * <ParcelViewerMap
 *   selectedPropertyLocation={{ lat: 40.7128, lng: -74.0060 }}
 *   onParcelChange={(grouped) => sheetOps.setSheetsFromMap(grouped)}
 * />
 */
export const ParcelViewerMap: React.FC<Props> = ({
  selectedPropertyLocation,
  onParcelChange,
  onOpenParcelViewerPopup,
}) => {
  const { t } = useTranslation(['purchase_flow'])

  const parcelViewerUrl = useMemo(
    () => `${process.env.REACT_APP_DOCESTATE_MAP_URL}?lat=${selectedPropertyLocation?.lat ?? 0}&lng=${selectedPropertyLocation?.lng ?? 0}`,
    [selectedPropertyLocation]
  )

  // Request the selected parcels from the map viewer.
  const requestParcelSelection = useCallback((frame: HTMLIFrameElement | null) => {
    if (frame instanceof HTMLIFrameElement && frame.contentWindow) {
      frame.contentWindow.postMessage({ type: 'getSelectedParcels' }, '*')
    }
  }, [])

  const handleSelectedParcels = useCallback((payload: string[]) => {
    // The format received is "district_section_parcelNumber".
    const selectedParcels = payload.map((data) => data.split('_'))

    const groupedData = selectedParcels.reduce((acc, [district, section, parcelNumber]) => {
      if (!acc[district]) acc[district] = {}
      if (!acc[district][section]) acc[district][section] = []
      acc[district][section].push(parcelNumber)
      return acc
    }, {} as GroupedParcels)

    onParcelChange?.(groupedData, payload)
  }, [onParcelChange])

  useEffect(() => {
    const frame = document.getElementById('parcel-viewer') as HTMLIFrameElement | null

    // Named handler so the SAME reference is added and removed. The legacy code
    // passed a fresh `() => {}` to `removeEventListener` and thus never detached.
    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== process.env.REACT_APP_DOCESTATE_MAP_URL) return

      const { type, payload } = e.data

      switch (type) {
        case 'selectedParcels':
          if (Array.isArray(payload)) handleSelectedParcels(payload) // string[], else Property
          break
        case 'parcelChange': // single parcel toggled → re-request the full selection
          requestParcelSelection(frame)
          break
        case 'error': {
          const sentryMessage = 'Docestate parcel view map ERROR'
          Sentry.captureException(sentryMessage, (scope) => {
            scope.clearBreadcrumbs()
            scope.setFingerprint([sentryMessage])
            scope.setExtra('payload', payload)
            return scope
          })
          break
        }
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleSelectedParcels, requestParcelSelection])

  return (
    <SectionedBorderBox
      marginBottom={2}
      title={
        <Stack direction="row" justifyContent="flex-start" alignItems="center" gap="1rem">

          <Typography color={GRAY_900} variant="text-lg" fontWeight={600}>
            {t('property_information_step.parcel_map_viewer')}
          </Typography>

          <Box onClick={onOpenParcelViewerPopup} sx={{ cursor: 'pointer' }}>
            <HelpOutlineIcon fontSize="large" sx={{ color: GRAY_900 }} />
          </Box>

        </Stack>
      }
    >

      {/* DOCESTATE PARCEL VIEWER */}
      <iframe
        id="parcel-viewer"
        src={parcelViewerUrl}
        className={styles.parcelViewer}
        title="Parcel Map Viewer"
      />

      <BorderBoxWrapper backgroundColor={BLUE_100} borderColor={BLUE_300} padding={1.5} marginTop="2rem">
        <Stack gap=".6rem">

          <Typography variant="text-sm" color={GRAY_900}>
            <Trans t={t} i18nKey="property_information_step.parcel_map_instruction.content" />
          </Typography>

          <MUIDivider margin={8} sx={{ borderColor: `${GRAY_400} !important` }} />

          <Typography variant="text-sm" color={GRAY_900}>
            <Trans t={t} i18nKey="property_information_step.parcel_map_instruction.important_note" />
          </Typography>

        </Stack>
      </BorderBoxWrapper>

    </SectionedBorderBox>
  )
}
